/**
 * useMarketOpen Hook
 *
 * Provides a centralized market-open event that widgets can subscribe to.
 * Instead of each widget setting its own timer, this hook manages a single
 * global timer and notifies all subscribers when the market opens.
 *
 * Benefits:
 * - Single timer instead of N timers (one per widget)
 * - Staggered callbacks to avoid thundering herd on API
 * - Consistent behavior across all chart widgets
 */

import { useEffect, useRef, useCallback } from 'react';
import { getMillisecondsUntilMarketOpen } from '../utils/marketDate';

// Global state for the market-open event
const marketOpenCallbacks: Set<() => void> = new Set();
let timerSet = false;
let timerId: ReturnType<typeof setTimeout> | null = null;

/**
 * Set up the global timer (called once when first subscriber registers)
 */
function ensureTimerSet(): void {
  if (timerSet) return;

  const msUntilOpen = getMillisecondsUntilMarketOpen();
  if (msUntilOpen <= 0) return;

  timerSet = true;
  timerId = setTimeout(() => {
    // Stagger callbacks to avoid thundering herd
    // Each callback fires 100ms apart
    let delay = 0;
    marketOpenCallbacks.forEach((callback) => {
      setTimeout(callback, delay);
      delay += 100;
    });

    // Reset for next day (in case app stays open overnight)
    timerSet = false;
    timerId = null;
  }, msUntilOpen);
}

/**
 * Hook to register a callback for market open
 *
 * @param onMarketOpen - Callback to fire when market opens (9:30 AM ET)
 *
 * @example
 * useMarketOpen(() => {
 *   // Clear data and refetch
 *   dataRef.current = null;
 *   refetch();
 * });
 */
export function useMarketOpen(onMarketOpen: () => void): void {
  // Use ref to avoid stale closure issues
  const callbackRef = useRef(onMarketOpen);
  callbackRef.current = onMarketOpen;

  // Stable callback wrapper
  const stableCallback = useCallback(() => {
    callbackRef.current();
  }, []);

  useEffect(() => {
    // Register callback
    marketOpenCallbacks.add(stableCallback);

    // Ensure timer is set
    ensureTimerSet();

    // Cleanup on unmount
    return () => {
      marketOpenCallbacks.delete(stableCallback);

      // If no more subscribers, clear the timer
      if (marketOpenCallbacks.size === 0 && timerId) {
        clearTimeout(timerId);
        timerSet = false;
        timerId = null;
      }
    };
  }, [stableCallback]);
}
